import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import {
  Search,
  Filter,
  PlusSquare,
  Edit,
  Trash2,
  StickyNoteIcon,
  Link as LinkIcon,
  FileText as DocIcon,
  XCircle,
  CheckCircle,
  PlayCircle,
  User,
  Phone,
  Mail,
  Landmark,
  Banknote,
  RotateCwIcon,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useSound } from "../../hooks/useSound";
import { useFileUpload } from "../../hooks/useFileUplode";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import PageForm from "./PageForm";
import { toast } from "react-toastify";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";

function Sound() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSound, setSelectedSound] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view, edit, create
  const [contentsList, setContentsList] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState({
    sound_type: "ALL",
  });
  const [soundData, setPageData] = useState({
    currentPage: 1,
    totalPages: 1,
    showing: 10,
    total: 0,
  });

  const queryClient = useQueryClient();

  const {
    fetchSound,
    createSound,
    updateContent,
    deleteContent,
    toggleSoundStatus,
    toggleDefaultSound,
    toggleIsExclusive,
  } = useSound({
    filters: { ...statusFilter, search_value: searchQuery },
    pagination: { page: soundData.currentPage, limit: 10 },
  });
  const { fileUpload, fileDelete } = useFileUpload();

  const contentsQuery = useQuery({
    queryKey: ["sound", soundData.currentPage, statusFilter, searchQuery],
    queryFn: fetchSound,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!contentsQuery.isLoading && contentsQuery.data) {
      const response = contentsQuery.data;
      setContentsList(response.data.sound || []);
      setPageData({
        currentPage: response.data.pagination?.currentPage || 1,
        totalPages: response.data.pagination?.lastPage || 1,
        total: response.data.pagination?.total || 0,
        showing: response.data.pagination?.showing || 10,
      });
    }
  }, [contentsQuery.data, contentsQuery.isLoading]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const openModal = (sound, mode) => {
    setSelectedSound(sound);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSound(null);
  };

  const handlePageChange = (sound) => {
    console.log(sound);
    setPageData((prev) => ({
      ...prev,
      currentPage: sound,
    }));
  };

  const handleUpdate = (sound) => {
    openModal(sound, "edit");
  };

  const handleDelete = {
    onDelete: (sound) =>
      mutation.deleteSound.mutate(sound._id, {
        onSuccess: (data) => {
          toast.success(data.message || "Sound Deleted");
          setDeleteModal(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to delete sound");
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (sound) => {
      setDeleteModal(true);
      setSelectedSound(sound);
    },
  };

  const toggleStatus = (sound) => {
    mutation.toggleStatus.mutate(sound._id, {
      onSuccess: (data) => {
        toast.success("Status updated successfully");
      },
      onError: (error) => toast.error("Failed to update status"),
    });
  };

  const toggleDefault = (sound) => {
    mutation.toggleDefault.mutate(sound._id, {
      onSuccess: (data) => {
        toast.success("Default status updated successfully");
      },
      onError: (error) => toast.error("Failed to update default status"),
    });
  };

  const mutation = {
    createSound: useMutation({
      mutationFn: (body) => {
        if (modalMode === "edit") {
          return updateContent(body);
        }
        return createSound(body);
      },
    }),
    deleteSound: useMutation({
      mutationFn: (id) => deleteContent(id),
    }),
    toggleStatus: useMutation({
      mutationFn: async (id) => await toggleSoundStatus(id),
    }),
    toggleDefault: useMutation({
      mutationFn: async (id) => await toggleDefaultSound(id),
    }),
    toggleExclusive: useMutation({
      mutationFn: async (id) => await toggleIsExclusive(id),
    }),
  };

  const handleSubmit = (body) => {
    if (modalMode === "edit") {
      body._id = selectedSound._id;
    }

    mutation.createSound.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["sound"]);
        toast.success(
          data?.data?.message ||
            `Sound ${modalMode === "edit" ? "Updated" : "Created"} successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save sound");
      },
    });
  };

  const toggleExclusive = (sound) => {
    mutation.toggleExclusive.mutate(sound._id, {
      onSuccess: (data) => {
        toast.success(data.message);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  return (
    <div>
      <main className="">
        {/* Header and Add Button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Sound</h1>
          <button
            onClick={() => openModal({}, "create")}
            className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusSquare className="mr-2 h-4 w-4" />
            Add New Sound
          </button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search sounds..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter.sound_type}
                onChange={(e) =>
                  setStatusFilter((prev) => ({
                    ...prev,
                    sound_type: e.target.value,
                  }))
                }
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="ALL">All</option>
                <option value="USER_ADDED">User Added Sounds</option>
                <option value="ADMIN_ADDED">Admin Added Sounds</option>
              </select>
              <button
                className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter((prev) => ({
                    ...prev,
                    sound_type: "ALL",
                  }));
                  setPageData((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                }}
              >
                <RotateCwIcon className="h-4 w-4" />
                <span className="ml-2">Reset</span>
              </button>
            </div>
          </div>

          {/* Table or Skeleton */}
          {contentsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Title
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Exclusive
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Audio
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Status
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Default
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Added By
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {contentsList.length > 0 ? (
                    contentsList.map((sound) => (
                      <tr key={sound._id} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-3 justify-items-start">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              {sound.is_exclusive ? (
                                <Landmark className="h-4 w-4 text-purple-800" />
                              ) : (
                                <Banknote
                                  className={`h-4 w-4 ${
                                    !sound.user_id && !sound.user_data
                                      ? "text-green-800"
                                      : "text-gray-800"
                                  } `}
                                />
                              )}
                            </div>
                            <div className="ml-3">
                              <div
                                className={`font-medium ${
                                  sound.is_exclusive
                                    ? "text-purple-800"
                                    : !sound.user_id && !sound.user_data
                                    ? "text-green-800"
                                    : "text-gray-800"
                                }`}
                                title={
                                  sound.is_exclusive
                                    ? "Exclusive, available with only Alert Customization"
                                    : "Normal, available with subscription"
                                }
                              >
                                {sound.title}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {mutation.toggleExclusive.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium select-none ${
                                !sound.user_id && !sound.user_data
                                  ? "cursor-pointer"
                                  : "cursor-not-allowed opacity-100"
                              } ${
                                sound.is_exclusive
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                              onClick={() => {
                                if (!sound.user_id && !sound.user_data) {
                                  setSelectedSound(sound);
                                  toggleExclusive(sound);
                                }
                              }}
                              title={
                                !sound.user_id && !sound.user_data
                                  ? "Toggle Exclusive"
                                  : "User Specific Data (Cannot make it exclusive Sound)"
                              }
                            >
                              {sound.is_exclusive ? (
                                <Landmark className="mr-1 h-3 w-3" />
                              ) : (
                                <Banknote className="mr-1 h-3 w-3" />
                              )}
                              {sound.is_exclusive ? "Exclusive" : "Normal"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 justify-items-start">
                          <div
                            className="flex items-center justify-center cursor-pointer"
                            onClick={() => window.open(sound.sound, "_blank")}
                          >
                            <PlayCircle className="h-5 w-5 text-gray-400" />
                            {/* <DocIcon  /> */}
                          </div>
                        </td>
                        <td className="px-4 py-3 justify-items-start">
                          {mutation.toggleStatus.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer select-none ${
                                sound.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                toggleStatus(sound);
                              }}
                              title="Toggle Status"
                            >
                              {sound.is_active ? (
                                <CheckCircle className="mr-1 h-3 w-3" />
                              ) : (
                                <XCircle className="mr-1 h-3 w-3" />
                              )}
                              {sound.is_active ? "Active" : "Inactive"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 justify-items-start">
                          {mutation.toggleDefault.isPending ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <svg
                                className="animate-spin h-4 w-4 text-gray-500"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                                ></path>
                              </svg>
                              <span className="ml-1">Updating</span>
                            </span>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium select-none ${
                                !sound.user_id && !sound.user_data
                                  ? "cursor-pointer"
                                  : "cursor-not-allowed opacity-100"
                              } ${
                                sound.is_default
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                              onClick={() => {
                                if (!sound.user_id && !sound.user_data) {
                                  toggleDefault(sound); // only toggle when allowed
                                }
                              }}
                              title={
                                !sound.user_id && !sound.user_data
                                  ? "Toggle Default"
                                  : "User Specific Data (Cannot make it as Default Sound)"
                              }
                            >
                              {!sound.user_id && !sound.user_data ? (
                                <>
                                  {sound.is_default ? (
                                    <>
                                      <CheckCircle className="mr-1 h-3 w-3" />
                                      Default
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="mr-1 h-3 w-3" />
                                      Not Default
                                    </>
                                  )}
                                </>
                              ) : (
                                "User Specific Data"
                              )}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 justify-items-center-safe">
                          <div
                            title="View User Details"
                            className="cursor-pointer"
                            onClick={() => openModal(sound, "viewUser")}
                          >
                            {sound?.user_data?.full_name || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 justify-items-start">
                          {moment(sound.created_at).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 justify-items-start">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openModal(sound, "view")}
                              className="text-gray-400 hover:text-gray-500"
                              title="View Details"
                            >
                              <StickyNoteIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdate(sound)}
                              className="text-blue-400 hover:text-blue-500"
                              title="Edit Sound"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              className="text-red-400 hover:text-red-500"
                              onClick={() => handleDelete.onOpen(sound)}
                              title="Delete Sound"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No sounds found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={soundData.currentPage}
            onPageChange={handlePageChange}
            showing={soundData.showing}
            totalPages={soundData.totalPages}
            total={soundData.total}
          />
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedSound)}
        title="Sound"
      />
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Sound Details"
                  : modalMode === "edit"
                  ? "Edit Sound"
                  : "Create Sound"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {modalMode === "viewUser" ? (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 h-5 w-5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={selectedSound?.user_data?.full_name || ""}
                      readOnly={true}
                      className="pl-10 pr-3 py-2 border rounded w-full"
                    />
                  </div>
                  <label className="block text-sm font-medium mt-4 mb-1">
                    Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 h-5 w-5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={selectedSound?.user_data?.phone || ""}
                      readOnly={true}
                      className="pl-10 pr-3 py-2 border rounded w-full"
                    />
                  </div>
                  <label className="block text-sm font-medium mt-4 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 h-5 w-5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={selectedSound?.user_data?.email || "Not Available"}
                      readOnly={true}
                      className="pl-10 pr-3 py-2 border rounded w-full"
                    />
                  </div>
                </div>
              ) : (
                <PageForm
                  mode={modalMode}
                  defaultValues={
                    selectedSound
                      ? {
                          ...selectedSound,
                          created_at: moment(selectedSound.created_at).format(
                            "YYYY-MM-DD"
                          ),
                          contentIds: selectedSound.contentIds || [],
                        }
                      : {}
                  }
                  onCancel={closeModal}
                  contentsList={contentsList}
                  onSubmit={(data) => handleSubmit(data)}
                  submissionLoading={mutation.createSound.isLoading}
                  fileUpload={fileUpload}
                  fileDelete={fileDelete}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Sound;
